import Playlist from "./Playlist";
import Util from "./Util";

const findScoreRegex = /(\d+) ?- ?(\d+)/;

const expectedTagFields = {
    "corner": ["team"],
    "end phase": ["phase"],
    "free kick": ["team"],
    "goal": ["team", "shot type", "scorer", "keeper"],
    "medical treatment": ["team", "player"],
    "offside": ["team", "player"],
    "penalty": ["team", "offending player", "player awarded"],
    "red card": ["team", "player", "reason"],
    "shot": ["team", "player", "on target", "shot type", "shot result", "keeper"],
    "start phase": ["team", "phase"],
    "substitution": ["team", "player in", "player out"],
    "throw-in": ["team"],
    "yellow card": ["team", "player"],
};

export default class GameEvent {
    constructor () {
        this.server = null;
        this.id = null;
        this.playlist = null;
        this.playlistId = null;
        this.gameId = null;
        this.tag = null;
        this.wallClockTime = null;
        this.videoOffsetMillisec = null;
        this.gameTime = null;
        this.timestamp = null;
        this.status = null;
        this.score = null;
        this.tentativeId = null;
        this.originalAction = null;
        this.raw = null;
    }

    static create (videoSource, videoOffset, tag, score, description, level2=false) {

        /* Cameraman will usually zoom in on players 1-4sec after an event occurs.
         * Based on empirical data, 4 seconds produces the most consistently memorable thumb */
        const thumbnailTimestamp = (videoOffset + 4) * 1000;
        const thumbnailUrl = Util.getThumbnailUrl(
            videoSource.name,
            thumbnailTimestamp
        );

        let preroll = 10000;
        let postroll = 20000;

        if (level2) {
            // Make a much longer clip if it was created by l2 to give some editing room
            preroll = 30000;
            postroll = 50000;
        } else if (tag.action === 'goal') {
            // Add some time for celebrations!
            postroll = 35000;
        }

        //Round timestamps to nearest 2sec segment
        let fromTimestamp = Math.max(0, Math.round(videoOffset/2) * 2 * 1000 - preroll);
        let toTimestamp = Math.round(videoOffset/2) * 2 * 1000 + postroll;

        const playlist = Playlist.create(
            videoSource, //videoAssetId
            Math.floor(fromTimestamp), //fromTimestamp
            Math.floor(toTimestamp), //toTimestamp
            description, //description
            tag, //tag
            thumbnailUrl, //thumbnailUrl
        );

        let newEvent = new GameEvent();

        newEvent.server = videoSource.server;
        newEvent.tag = tag;
        newEvent.videoOffsetMillisec = Math.round(videoOffset * 1000);
        newEvent.gameId = videoSource.game.id;
        newEvent.playlist = playlist;
        newEvent.playlistId = playlist.id;
        newEvent.score = score;
        newEvent.originalAction = tag.action;

        if (level2) {
            // We need a way to differentiate between unique objects. Normally we have
            // event.id, but this tentative object does not yet have an ID.
            newEvent.tentativeId = "_" + (Math.floor(Math.random() * 123456789));
            newEvent.status = "tagging_level_2";
        }

        return newEvent;
    }

    static createFromAbsoluteTimestamp (videoSource, timestamp, tag, score, description, level2=false) {
        // Note: Here we are comparing a clientside timestamp against serverside timestamp. Not perfect.
        const streamStartTime = new Date(videoSource.start_timestamp);
        const videoOffset = (timestamp - streamStartTime) / 1000;

        let newEvent = GameEvent.create(videoSource, videoOffset, tag, score, description, level2);
        newEvent.wallClockTime = timestamp.toISOString();

        return newEvent;
    }

    static load (event, server) {

        if (!event.tag) return null;
        let playlist = Playlist.load(event.playlist);
        if (playlist === null) return null;

        let newEvent = new GameEvent();
        newEvent.server = server;
        newEvent.id = event.id;
        newEvent.playlist = playlist;
        newEvent.playlistId = playlist.id;
        newEvent.gameId = event.game_id;
        newEvent.tag = event.tag;
        newEvent.originalAction = event.tag.action;
        newEvent.gameTime = event.game_time;
        newEvent.timestamp = Date.parse(event.wall_clock_time);
        newEvent.status = event.status;
        newEvent.videoOffsetMillisec = playlist.fromTimestamp + (
            newEvent.timestamp - playlist.recordingTime);
        newEvent.raw = event;

        let scoreSearch;
        if (event.score) {
            scoreSearch = findScoreRegex.exec(event.score);
        } else {
            // Try to look for a string like "2-1" in description to indicate score, else keep score null
            scoreSearch = findScoreRegex.exec(playlist.description);
        }
        if (scoreSearch !== null) {
            newEvent.score = [ parseInt(scoreSearch[1], 10), parseInt(scoreSearch[2], 10) ];
        }

        return newEvent;
    }

    key () {
        let id = this.id === null ? this.tentativeId : this.id;
        return this.server.name + id;
    }

    gameKey () {
        return this.server.name + "_" + this.gameId;
    }

    toString () {
        if (this.playlist === null) { return "{0}: <no video>".format(this.id); }
        return this.playlist.description;
    }

    gameTimeString () {
        return this.gameTime === null ? "" : "{0}´".format(Math.floor(this.gameTime / 60));
    }

    gameString () {
        if (this.playlist === null || this.playlist.game === null) return " - ";
        const game = this.playlist.game;
        if (game.home_team === null || game.visiting_team === null) return " - ";
        return `${game.home_team.name} v ${game.visiting_team.name}`;
    }

    getMissingExpectedFields () {
        const expectedFields = expectedTagFields[this.tag.action];
        if (expectedFields === undefined) return [];
        return expectedFields.filter(f => this.tag[f] === undefined);
    }

    toJSON () {
        let data = {
            playlist: this.playlist.toJSON(),
            tag: this.tag,
            id: this.id === null ? undefined : this.id,
            game_id: this.gameId,
            status: this.status
        };

        if (this.wallClockTime === null) {
            data.video_asset_offset = this.videoOffsetMillisec;
        } else {
            data.wall_clock_time = this.wallClockTime;
        }

        return data;
    }

    _deleteAndRePost (body, onSuccess, errorCallback) {
        console.log("Tag action has changed from",
            this.originalAction, "to", this.tag.action, "Doing a DELETE+POST");

        const ts = new Date(this.timestamp).toLocaleString();
        // For simplicity we disallow changing phase events, since that can have weird effects
        if (this.originalAction === "start phase" ||
            this.originalAction === "end phase" ||
            this.originalAction === "end of game") {
            errorCallback(`Cannot change type of phase events. Please explicitly delete the event and create a new event (timestamp=${ts})`);
            return;
        }

        this.delete(false, () => {

            this.server.post("/event/", body, (data, headers) => {

                //Get new ID from Location header
                const loc = headers.get("Location");
                this.id = loc.match(/.+\/event\/(.+)/)[1];
                this.originalAction = this.tag.action;
                console.log("Posted new event: ID =", this.id);
                onSuccess();
            }, (error) => {
                error = `${error} IMPORTANT: The old ${this.originalAction} event was deleted successfully, but the new ${this.tag.action} event failed. Try re-creating this event (timestamp: ${ts}).`;
                errorCallback(error);
            });
        }, errorCallback);
    }

    publish (successCallback, errorCallback) {

        const updateExistingEvent = this.id !== null;
        const onSuccess = () => {
            this.originalAction = this.tag.action;
            successCallback();
        };

        const body = this.toJSON();
        body.status = (this.status === null) ? "tagging_level_1" : "tagging_level_2";

        let endpoint = "/event/";
        if (updateExistingEvent) {

            if (this.tag.action !== this.originalAction) {
                // This is done due to Norkring / SMC - they don't support event-type mutating
                // Therefore, we issue both a DELETE and a new POST
                this._deleteAndRePost(body, onSuccess, errorCallback);
            } else {
                endpoint += this.id;
                this.server.put(endpoint, body, onSuccess, errorCallback);
            }
        } else {
            this.server.post(endpoint, body, onSuccess, errorCallback);
        }
    }

    delete (doDeletePlaylist, successCallback, errorCallback) {
        const url = this.server.createUrl('/event/' + this.id, {
            delete_playlist: doDeletePlaylist,
        });
        this.server.delete(url, null, successCallback, errorCallback);
    }
}



// WEBPACK FOOTER //
// ./src/js/include/GameEvent.js